import React, { useEffect, useState, useCallback } from 'react';
import { collection, getDocs, query, orderBy, limit, startAfter, doc, deleteDoc } from 'firebase/firestore';
import { Button, Table, Modal, Image } from 'react-bootstrap';
import { db } from '../../../firebase';
import { Link } from 'react-router-dom';
import { getStorage, ref as storageRef, deleteObject } from 'firebase/storage';
import { useAuth } from '../../../components/context/AuthContext';
import { createLogger } from '../../../utils/logger';

const logger = createLogger('PaginatedSupermarkets');

const PaginatedSupermarkets = () => {
  const [supermarkets, setSupermarkets] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState('');

  const [showImagesModal, setShowImagesModal] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [showHoursModal, setShowHoursModal] = useState(false);
  const [selectedHours, setSelectedHours] = useState([]);
  const storage = getStorage();
  const { userRole } = useAuth();

  const SUPERMARKETS_COLLECTION = 'Supermarkets';
  const ITEMS_PER_PAGE = 5;

  // Effect for initial fetch on mount
  useEffect(() => {
    const initialFetch = async () => {
      setInitialLoading(true);
      setError(''); // Clear previous errors
      try {
        const supermarketsQuery = query(
          collection(db, SUPERMARKETS_COLLECTION),
          orderBy('created_at', 'desc'),
          limit(ITEMS_PER_PAGE)
        );
        const querySnapshot = await getDocs(supermarketsQuery);
        const fetchedSupermarkets = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setSupermarkets(fetchedSupermarkets);
        if (querySnapshot.docs.length > 0) {
            setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
        } else {
            setLastDoc(null); 
        }
        setHasMore(querySnapshot.docs.length === ITEMS_PER_PAGE);
      } catch (e) {
        logger.error("[PaginatedSupermarkets] Error fetching initial supermarkets:", e);
        setError('Failed to load supermarkets. Please try again.');
      }
      setInitialLoading(false);
    };
    initialFetch();
  }, []); // Empty dependency array: runs only once on mount

  // Function for fetching more supermarkets (pagination)
  const fetchMoreSupermarkets = useCallback(async () => {
    if (!hasMore) { // No need to check lastDoc here, hasMore is the primary flag
        return;
    }
    if (!lastDoc) { // Safety check, should not happen if hasMore is true and initial fetch set lastDoc
        setHasMore(false); // Prevent further calls if lastDoc is missing
        return;
    }

    setLoadingMore(true);
    setError(''); // Clear previous errors
    try {
      const supermarketsQuery = query(
        collection(db, SUPERMARKETS_COLLECTION),
        orderBy('created_at', 'desc'),
        startAfter(lastDoc),
        limit(ITEMS_PER_PAGE)
      );
      const querySnapshot = await getDocs(supermarketsQuery);
      const fetchedSupermarkets = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setSupermarkets((prev) => [...prev, ...fetchedSupermarkets]);
      if (querySnapshot.docs.length > 0) {
        setLastDoc(querySnapshot.docs[querySnapshot.docs.length - 1]);
      }
      setHasMore(querySnapshot.docs.length === ITEMS_PER_PAGE);
    } catch (e) {
      logger.error("[PaginatedSupermarkets] Error fetching more supermarkets:", e);
      setError('Failed to load more supermarkets. Please try again.');
    }
    setLoadingMore(false);
  }, [lastDoc, hasMore]); // Dependencies for useCallback

  const handleShowImages = (media) => {
    setSelectedMedia(media || []);
    setShowImagesModal(true);
  };
  const handleCloseImages = () => setShowImagesModal(false);

  const handleShowHours = (hours) => {
    setSelectedHours(hours || []);
    setShowHoursModal(true);
  };
  const handleCloseHours = () => setShowHoursModal(false);

  const handleDeleteSupermarket = async (supermarketId, mediaToDelete) => {
    if (userRole !== 'Admin') {
        alert("You are not authorized to delete supermarkets.");
        return;
    }
    if (!window.confirm(`Are you sure you want to delete supermarket ${supermarketId}? This action cannot be undone.`)) {
      return;
    }
    setDeleting(supermarketId);
    setError('');
    try {
      const supermarketDocRef = doc(db, SUPERMARKETS_COLLECTION, supermarketId);
      await deleteDoc(supermarketDocRef);
      
      if (mediaToDelete && mediaToDelete.length > 0) {
        for (const mediaItem of mediaToDelete) {
          if (mediaItem.path && typeof mediaItem.path === 'string') { // Check path exists and is a string
            try {
              const imageFileRef = storageRef(storage, mediaItem.path);
              await deleteObject(imageFileRef);
            } catch (storageError) {
              logger.error(`[PaginatedSupermarkets] Failed to delete image ${mediaItem.path} from storage:`, storageError);
              setError(prev => `${prev  } Note: Failed to delete some images from storage (${storageError.code}).`);
            }
          }
        }
      }
      setSupermarkets(prevSupermarkets => prevSupermarkets.filter(s => s.id !== supermarketId));
      alert('Supermarket deleted successfully.');
    } catch (e) {
      setError(`Failed to delete supermarket. ${  e.message}`);
      logger.error("[PaginatedSupermarkets] Error deleting supermarket:", e);
    }
    setDeleting(null);
  };

  const getDeleteButtonDisabledState = (supermarketId) => {
    const isDisabled = deleting === supermarketId || initialLoading;
    return isDisabled;
  };

  return (
    <div>
      {error && <p className="text-danger">{error}</p>}
      {initialLoading && supermarkets.length === 0 && <p>Loading...</p>}

      <Table bordered hover responsive>
        <thead>
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Address</th>
            <th>Delivery Phone</th>
            <th>SEO Name</th>
            <th>Status</th>
            <th>Delivery</th>
            <th>Delivery Fee</th>
            <th>Max Distance</th>
            <th>Full Payment</th>
            <th>Created By</th>
            <th>Created At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {supermarkets.length === 0 && !initialLoading && (
            <tr>
              <td colSpan="13" className="text-center">No supermarkets found.</td>
            </tr>
          )}
          {supermarkets.map((supermarket, index) => (
            <tr key={supermarket.id}>
              <td>{index + 1}</td>
              <td>{supermarket.display_name}</td>
              <td>{supermarket.address}</td>
              <td>
                {supermarket.delivery_phone ? (
                  <a href={`tel:${supermarket.delivery_phone}`} className="text-decoration-none">
                    {supermarket.delivery_phone}
                  </a>
                ) : (
                  <span className="text-muted">Not set</span>
                )}
              </td>
              <td>
                {supermarket.seo_name ? (
                  <code className="bg-light px-2 py-1 rounded">{supermarket.seo_name}</code>
                ) : (
                  <span className="text-muted">Not set</span>
                )}
              </td>
              <td>
                <span className={`badge ${supermarket.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                  {supermarket.status || 'active'}
                </span>
              </td>
              <td>
                <span className={`badge ${supermarket.delivery_enabled ? 'bg-info' : 'bg-light text-dark'}`}>
                  {supermarket.delivery_enabled ? 'Enabled' : 'Disabled'}
                </span>
              </td>
              <td>
                {supermarket.delivery_fee != null && supermarket.delivery_fee > 0 ? (
                  `$${Number(supermarket.delivery_fee).toFixed(2)}`
                ) : (
                  <span className="text-muted">Not set</span>
                )}
              </td>
              <td>
                {supermarket.max_delivery_distance != null && supermarket.max_delivery_distance > 0 ? (
                  `${Number(supermarket.max_delivery_distance)} km`
                ) : (
                  <span className="text-muted">Not set</span>
                )}
              </td>
              <td>
                <span className={`badge ${supermarket.full_payment_enabled ? 'bg-success' : 'bg-light text-dark'}`}>
                  {supermarket.full_payment_enabled ? 'Enabled' : 'Disabled'}
                </span>
              </td>
              <td>{supermarket.created_by?.email || 'N/A'}</td>
              <td>{supermarket.created_at?.seconds ? new Date(supermarket.created_at.seconds * 1000).toLocaleDateString() : 'N/A'}</td>
              <td>
                <div className="d-flex flex-column flex-sm-row gap-1">
                  <Button
                    variant="outline-info"
                    size="sm"
                    className="flex-fill"
                    onClick={() => handleShowImages(supermarket.media)}
                    disabled={!supermarket.media || supermarket.media.length === 0 || initialLoading}
                  >
                    Images
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="flex-fill"
                    onClick={() => handleShowHours(supermarket.opening_hours)}
                    disabled={!supermarket.opening_hours || supermarket.opening_hours.length === 0 || initialLoading}
                  >
                    Hours
                  </Button>
                  <Link to={`edit/${supermarket.id}`} className={`btn btn-outline-warning btn-sm flex-fill ${initialLoading ? 'disabled' : ''}`}>
                    Edit
                  </Link>
                  {userRole === 'Admin' && (
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      className="flex-fill"
                      onClick={() => handleDeleteSupermarket(supermarket.id, supermarket.media)}
                      disabled={getDeleteButtonDisabledState(supermarket.id)} 
                    >
                      {deleting === supermarket.id ? 'Deleting...' : 'Delete'}
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {hasMore && (
        <div className="text-center mt-4">
          <Button
            onClick={fetchMoreSupermarkets}
            disabled={loadingMore || initialLoading} 
            variant="primary"
            className="px-4"
            style={{ minWidth: '200px' }}
          >
            {loadingMore ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                Loading More...
              </>
            ) : (
              'Load More Supermarkets'
            )}
          </Button>
        </div>
      )}

      <Modal show={showImagesModal} onHide={handleCloseImages}>
        <Modal.Header closeButton><Modal.Title>Supermarket Images</Modal.Title></Modal.Header>
        <Modal.Body>
          {selectedMedia.length === 0 ? <p>No images available.</p> : (
            <div className="d-flex flex-wrap">
              {selectedMedia.map((media, idx) => (
                media.url && <Image key={idx} src={media.url} alt={`Media ${idx + 1}`} className="img-thumbnail me-2 mb-2" style={{ maxWidth: '100px', height: 'auto' }} />
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={handleCloseImages}>Close</Button></Modal.Footer>
      </Modal>

      <Modal show={showHoursModal} onHide={handleCloseHours}>
        <Modal.Header closeButton><Modal.Title>Opening Hours</Modal.Title></Modal.Header>
        <Modal.Body>
          {selectedHours.length === 0 ? (
            <p className="text-muted">No opening hours set for this supermarket.</p>
          ) : (
            <Table striped bordered className="mb-0">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Hours</th>
                </tr>
              </thead>
              <tbody>
                {selectedHours.map((hour, idx) => (
                  <tr key={idx}>
                    <td className="fw-semibold">{hour.day}</td>
                    <td>{hour.hours}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={handleCloseHours}>Close</Button></Modal.Footer>
      </Modal>
    </div>
  );
};

export default PaginatedSupermarkets;